import { Suspense, useCallback, useEffect, useMemo, type ReactNode } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { NotFoundPage } from "@/components/feedback/NotFoundPage";
import { RouteLoadingFallback } from "@/components/feedback/RouteLoadingFallback";
import { ShadowPageBoundary } from "@/components/layout/ShadowPageBoundary";
import { env } from "@/config/env";
import { ADMIN_PAGES, type NavigationPage } from "@/app/navigation";
import PremiumAdminAppShell from "@/layout/PremiumAdminAppShell";
import { PAGE_COMPONENTS } from "@/app/pageRegistry";
import { useAuth } from "@/api/auth";
import LoginPage from "@/pages/auth/LoginPage";
import ForgotPasswordPage from "@/pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/auth/ResetPasswordPage";
import ChangePasswordPage from "@/pages/auth/ChangePasswordPage";
import MfaPage from "@/pages/auth/MfaPage";
import {
  mapCommandsToSearchRecords,
  mapPrincipalToShellUser,
  normalizeNotifications,
  useAppShell,
} from "@/hooks/useAppShell";

const normalizePath = (path: string): string => {
  if (path === "/") return path;
  return path.replace(/\/+$/, "") || "/";
};

function hasPermission(grants: string[], required: string): boolean {
  return (
    grants.includes("*") ||
    grants.includes(required) ||
    grants.some((g) => g.endsWith(".*") && required.startsWith(g.slice(0, -1)))
  );
}

export default function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, principal, logout } = useAuth();
  const normalizedPath = normalizePath(location.pathname);

  const activePage = useMemo(
    () => ADMIN_PAGES.find((page) => normalizePath(page.route) === normalizedPath),
    [normalizedPath],
  );

  const { data: appShellData } = useAppShell(isAuthenticated);

  const permissions = useMemo(
    () => appShellData?.principal?.permissions ?? principal?.permissions ?? [],
    [appShellData?.principal?.permissions, principal?.permissions],
  );

  const shellUser = useMemo(
    () => (appShellData?.principal ? mapPrincipalToShellUser(appShellData.principal) : undefined),
    [appShellData?.principal],
  );

  const searchRecords = useMemo(
    () => (appShellData?.commands ? mapCommandsToSearchRecords(appShellData.commands) : []),
    [appShellData?.commands],
  );

  const notifications = useMemo(
    () => (appShellData?.notifications ? normalizeNotifications(appShellData.notifications) : undefined),
    [appShellData?.notifications],
  );

  const badges = appShellData?.badges;

  const navigateFromShell = useCallback(
    (page: NavigationPage): void => {
      navigate(page.route);
    },
    [navigate],
  );

  const handleLogout = useCallback(async () => {
    await logout();
    navigate("/", { replace: true });
  }, [logout, navigate]);

  useEffect(() => {
    document.title = activePage ? `${activePage.shortLabel} · ${env.appName}` : env.appName;
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [activePage]);

  // Auto-reload if a lazy-loaded chunk fails to fetch (stale SPA after a new
  // build) — capped at 2 attempts per session so a persistent failure can
  // never become a reload loop.
  useEffect(() => {
    const CHUNK_RELOAD_KEY = "evzone_chunk_reload";
    const MAX_RELOADS = 2;
    const tryReload = () => {
      const attempts = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? "0");
      if (attempts < MAX_RELOADS) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, String(attempts + 1));
        window.location.reload();
      }
    };
    const isChunkError = (message: string) =>
      /Failed to fetch dynamically imported module|Loading chunk|Loading CSS chunk/i.test(message);
    const handleError = (event: ErrorEvent) => {
      if (typeof event.message === "string" && isChunkError(event.message)) {
        tryReload();
      }
    };
    const handleRejection = (event: PromiseRejectionEvent) => {
      const message = String(event.reason ?? "");
      if (isChunkError(message)) {
        tryReload();
      }
    };
    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleRejection);
    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleRejection);
    };
  }, []);

  // Loading state while restoring session
  if (isLoading) {
    return (
      <div className="ev-auth-loading">
        <div className="ev-auth-loading__spinner" aria-label="Loading session" />
        <p>Restoring session…</p>
        <style>{`
          .ev-auth-loading { min-height:100vh; display:grid; place-items:center; color:#6e7b78; }
          .ev-auth-loading__spinner { width:32px; height:32px; border:3px solid #e2e7e5; border-top-color:#ff7a00; border-radius:50%; animation:ev-spin 1s linear infinite; }
          @keyframes ev-spin { to { transform:rotate(360deg); } }
        `}</style>
      </div>
    );
  }

  // Public and protected authentication routes render without the workspace shell
  if (normalizedPath.startsWith("/auth/")) {
    if (normalizedPath === "/auth/login") return <LoginPage />;
    if (normalizedPath === "/auth/forgot-password") return <ForgotPasswordPage />;
    if (normalizedPath === "/auth/reset-password") return <ResetPasswordPage />;
    if (!isAuthenticated) return <LoginPage />;
    if (normalizedPath === "/auth/change-password") return <ChangePasswordPage />;
    if (normalizedPath === "/auth/mfa") return <MfaPage />;
    return (
      <NotFoundPage
        onHome={() =>
          navigate(isAuthenticated ? "/media-content/command-center" : "/auth/login")
        }
      />
    );
  }

  // Show login for unauthenticated users on any workspace route
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Redirect root to command center
  if (normalizedPath === "/") {
    return <Navigate replace to="/media-content/command-center" />;
  }

  // Permission check for directly entered routes
  const canAccessPage = activePage ? hasPermission(permissions, activePage.permission) : true;

  const fallbackPage = ADMIN_PAGES[0];
  const shellPage = activePage ?? fallbackPage;
  const Page = activePage && canAccessPage ? PAGE_COMPONENTS[activePage.id] : undefined;

  let content: ReactNode = undefined;

  if (!activePage) {
    content = <NotFoundPage onHome={() => navigate(fallbackPage.route)} />;
  } else if (!canAccessPage) {
    content = (
      <div className="ev-access-denied">
        <h1>Access denied</h1>
        <p>You do not have permission to view this workspace.</p>
        <button onClick={() => navigate(fallbackPage.route)}>Go to Command Center</button>
        <style>{`
          .ev-access-denied { padding:48px; text-align:center; color:#17201e; }
          .ev-access-denied h1 { font-size:28px; margin-bottom:12px; }
          .ev-access-denied button { margin-top:20px; padding:12px 20px; border:none; border-radius:10px; background:#ff7a00; color:#fff; font-weight:700; cursor:pointer; }
        `}</style>
      </div>
    );
  } else if (Page) {
    content = (
      <ShadowPageBoundary>
        <Suspense fallback={<RouteLoadingFallback />}>
          <Page />
        </Suspense>
      </ShadowPageBoundary>
    );
  }

  return (
    <PremiumAdminAppShell
      activePage={shellPage.id}
      onNavigate={navigateFromShell}
      contentClassName={activePage ? "route-content" : undefined}
      environment="Production"
      permissions={permissions}
      user={shellUser}
      searchRecords={searchRecords}
      notifications={notifications}
      badges={badges}
      onLogout={handleLogout}
    >
      {content}
    </PremiumAdminAppShell>
  );
}
